import React, { useEffect, useState, useRef } from 'react';
import { Eye, Users, Plane, Globe2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple count-up animation
const useCountUp = (target, duration = 1600) => {
  const [value, setValue] = useState(0);
  const startTimeRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!target || target <= 0) return;
    const startVal = 0;
    const step = (ts) => {
      if (!startTimeRef.current) startTimeRef.current = ts;
      const progress = Math.min((ts - startTimeRef.current) / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(startVal + (target - startVal) * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return value;
};

const formatNumber = (n) => n.toLocaleString('en-IN');

export const VisitorCounter = () => {
  const [visitors, setVisitors] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const alreadyCounted = sessionStorage.getItem('dte_visit_counted');
    const endpoint = alreadyCounted ? 'GET' : 'POST';

    fetch(`${API}/visits`, { method: endpoint })
      .then((r) => r.json())
      .then((data) => {
        setVisitors(data.count || 0);
        setLoaded(true);
        if (!alreadyCounted) sessionStorage.setItem('dte_visit_counted', '1');
      })
      .catch(() => setLoaded(true));
  }, []);

  const animatedCount = useCountUp(visitors);

  // Static stats to complement the live counter
  const stats = [
    {
      icon: Eye,
      label: 'Total Visitors',
      value: animatedCount,
      color: 'from-blue-500 to-cyan-500',
      isLive: true,
    },
    {
      icon: Users,
      label: 'Happy Clients',
      value: '5,000+',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: Plane,
      label: 'Tickets Issued',
      value: '12,500+',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: Globe2,
      label: 'Countries Served',
      value: '80+',
      color: 'from-green-500 to-teal-500',
    },
  ];

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-blue-50/60 border-t border-gray-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center px-4 py-2 bg-white shadow-sm border border-gray-200 rounded-full text-sm font-medium text-gray-700 mb-3">
            <span className="relative flex h-2 w-2 mr-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            Live Site Stats
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Trusted by Travelers Around the World
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-testid="visitor-stats">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className="relative bg-white rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-all border border-gray-100 group overflow-hidden"
              >
                <div
                  className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${s.color} opacity-10 group-hover:opacity-20 transition-opacity`}
                ></div>
                <div
                  className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} text-white mb-3 shadow-sm`}
                >
                  <Icon size={20} />
                </div>
                <div
                  className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-1 tabular-nums"
                  data-testid={s.isLive ? 'visitor-count' : undefined}
                >
                  {s.isLive ? (loaded ? formatNumber(s.value) : '···') : s.value}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">
                  {s.label}
                  {s.isLive && (
                    <span className="ml-1 text-green-600 font-semibold">• Live</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
